import { useCallback, useRef, useState } from "react";
import AnchoredPanel from "../../../GlobalComponents/AnchoredPanel/AnchoredPanel";

/**
 * The grid's Excel button: download the blank template, download the KPIs
 * the filters show, or upload a filled workbook for review.
 *
 * @param {object} props
 * @param {string} [props.busy] what is running, shown in place of the label
 * @param {boolean} [props.uploadLocked] unsaved grid edits — an upload would race them
 * @param {string} [props.lockedReason] set when the workbook cannot be used at all, e.g. several years are showing
 * @param {(template: boolean) => void} props.onDownload
 * @param {(file: File) => void} props.onUpload
 */
export default function ExcelMenu({ busy, uploadLocked, lockedReason, onDownload, onUpload }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const fileRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  const choose = (action) => {
    setOpen(false);
    action();
  };

  return (
    <div className="kgb-colpicker" ref={rootRef}>
      <button
        type="button"
        className="kgb-btn"
        aria-expanded={open}
        disabled={!!busy}
        onClick={() => setOpen((value) => !value)}
      >
        {busy || "Excel ▾"}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) onUpload(file);
        }}
      />
      {open ? (
        <AnchoredPanel anchorRef={rootRef} onClose={close} className="kgb-picker kgb-excelmenu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="kgb-picker-option"
            disabled={!!lockedReason}
            title={lockedReason}
            onClick={() => choose(() => onDownload(true))}
          >
            Download blank template
          </button>
          <button
            type="button"
            role="menuitem"
            className="kgb-picker-option"
            disabled={!!lockedReason}
            title={lockedReason}
            onClick={() => choose(() => onDownload(false))}
          >
            Download KPIs (current filters)
          </button>
          <button
            type="button"
            role="menuitem"
            className="kgb-picker-option"
            disabled={uploadLocked || !!lockedReason}
            title={lockedReason || (uploadLocked ? "Save or discard your grid changes first" : undefined)}
            onClick={() => choose(() => fileRef.current?.click())}
          >
            Upload Excel…
          </button>
        </AnchoredPanel>
      ) : null}
    </div>
  );
}
